﻿import React, { useState, useEffect, useContext, useRef } from "react";
import "emoji-mart/css/emoji-mart.css";
import { useParams } from "react-router-dom";
import { Picker } from "emoji-mart";
import clsx from "clsx";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import InputBase from "@material-ui/core/InputBase";
import CircularProgress from "@material-ui/core/CircularProgress";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import { green } from "@material-ui/core/colors";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import IconButton from "@material-ui/core/IconButton";
import MoreVert from "@material-ui/icons/MoreVert";
import MoodIcon from "@material-ui/icons/Mood";
import SendIcon from "@material-ui/icons/Send";
import CancelIcon from "@material-ui/icons/Cancel";
import ClearIcon from "@material-ui/icons/Clear";
import MicIcon from "@material-ui/icons/Mic";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import HighlightOffIcon from "@material-ui/icons/HighlightOff";
import {
  FormControlLabel,
  Hidden,
  Menu,
  MenuItem,
  Switch,
  Typography,
} from "@material-ui/core";
import ClickAwayListener from "@material-ui/core/ClickAwayListener";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import RecordingTimer from "./RecordingTimer";
import { ReplyMessageContext } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import toastError from "../../errors/toastError";

const getSupportedAudioMimeType = () => {
  if (typeof window === "undefined" || typeof window.MediaRecorder === "undefined") {
    return "";
  }

  const candidates = [
    "audio/ogg;codecs=opus",
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4"
  ];

  return candidates.find(type => window.MediaRecorder.isTypeSupported(type)) || "";
};

const getAudioFileExtension = mimeType => {
  if (!mimeType) {
    return "webm";
  }

  if (mimeType.includes("ogg")) {
    return "ogg";
  }

  if (mimeType.includes("mp4")) {
    return "m4a";
  }

  return "webm";
};

const useStyles = makeStyles(theme => ({
  mainWrapper: {
    background:
      theme.palette.type === "dark"
        ? "linear-gradient(180deg, rgba(15, 23, 42, 0.98) 0%, rgba(17, 24, 39, 0.98) 100%)"
        : "#eee",
    color: theme.palette.text.primary,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    borderTop: "1px solid rgba(0, 0, 0, 0.12)",
    [theme.breakpoints.down("sm")]: {
      position: "fixed",
      bottom: 0,
      width: "100%",
    },
  },

  newMessageBox: {
    background:
      theme.palette.type === "dark"
        ? "rgba(15, 23, 42, 0.92)"
        : "#eee",
    width: "100%",
    display: "flex",
    padding: "7px",
    alignItems: "center",
  },

  messageInputWrapper: {
    padding: 6,
    marginRight: 7,
    background:
      theme.palette.type === "dark"
        ? "rgba(17, 24, 39, 0.92)"
        : "#fff",
    display: "flex",
    borderRadius: 20,
    flex: 1,
    position: "relative",
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(45, 212, 191, 0.20)"
        : "rgba(148, 163, 184, 0.28)"
    }`,
  },

  messageInput: {
    paddingLeft: 10,
    flex: 1,
    border: "none",
    color: theme.palette.text.primary,
    "&::placeholder": {
      color: theme.palette.text.secondary,
      opacity: 1
    }
  },

  sendMessageIcons: {
    color: theme.palette.text.secondary,
  },

  uploadInput: {
    display: "none",
  },

  viewMediaInputWrapper: {
    display: "flex",
    padding: "10px 13px",
    position: "relative",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(15, 23, 42, 0.94)"
        : "#eee",
    borderTop: `1px solid ${theme.palette.divider}`,
  },

  emojiBox: {
    position: "absolute",
    bottom: 63,
    width: 40,
    borderTop: "1px solid #e8e8e8",
  },

  circleLoading: {
    color: green[500],
    opacity: "70%",
    position: "absolute",
    top: "20%",
    left: "50%",
    marginLeft: -12,
  },

  audioLoading: {
    color: green[500],
    opacity: "70%",
  },

  recorderWrapper: {
    display: "flex",
    alignItems: "center",
    alignContent: "middle",
  },

  cancelAudioIcon: {
    color: theme.palette.error.main,
  },

  sendAudioIcon: {
    color: theme.palette.success.main,
  },

  replyginMsgWrapper: {
    display: "flex",
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
    paddingTop: 8,
    paddingLeft: 73,
    paddingRight: 7,
  },

  replyginMsgContainer: {
    flex: 1,
    marginRight: 5,
    overflowY: "hidden",
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(148, 163, 184, 0.14)"
        : "rgba(0, 0, 0, 0.05)",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },

  replyginMsgBody: {
    padding: 10,
    height: "auto",
    display: "block",
    whiteSpace: "pre-wrap",
    overflow: "hidden",
  },

  replyginContactMsgSideColor: {
    flex: "none",
    width: "4px",
    backgroundColor: "#35cd96",
  },

  replyginSelfMsgSideColor: {
    flex: "none",
    width: "4px",
    backgroundColor: "#6bcbef",
  },

  messageContactName: {
    display: "flex",
    color: theme.palette.primary.main,
    fontWeight: 500,
  },
  messageQuickAnswersWrapper: {
    margin: 0,
    position: "absolute",
    bottom: "50px",
    background: theme.palette.background.paper,
    padding: "2px",
    border: `1px solid ${theme.palette.divider}`,
    left: 0,
    width: "100%",
    "& li": {
      listStyle: "none",
      "& a": {
        display: "block",
        padding: "8px",
        textOverflow: "ellipsis",
        overflow: "hidden",
        maxHeight: "32px",
        "&:hover": {
          background: theme.palette.action.hover,
          cursor: "pointer",
        },
      },
    },
  },
  mentionSuggestionsWrapper: {
    margin: 0,
    position: "absolute",
    bottom: "50px",
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    left: 0,
    width: "100%",
    maxHeight: 220,
    overflowY: "auto",
    zIndex: 5,
  },
  mentionSuggestionItem: {
    paddingTop: 4,
    paddingBottom: 4,
  },
  mentionSuggestionItemActive: {
    backgroundColor: theme.palette.action.hover,
  },
  readOnlyNotice: {
    width: "100%",
    padding: "8px 14px 0",
    color: theme.palette.text.secondary,
    fontSize: "0.78rem",
  },
  toggleControl: {
    margin: 0,
    marginRight: 7,
    color: theme.palette.text.secondary,
  },
  templateInput: {
    width: "100%",
    background: theme.palette.background.paper,
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    padding: "6px 10px",
    color: theme.palette.text.primary,
    "& input": {
      color: theme.palette.text.primary,
    },
    "& input::placeholder": {
      color: theme.palette.text.secondary,
      opacity: 1,
    },
  },
  templateHint: {
    color: theme.palette.text.secondary,
    padding: "0 6px",
  },
  templateModeNotice: {
    padding: "8px 10px",
    color: theme.palette.text.secondary,
    width: "100%",
  },
}));

const MessageInput = ({
  ticketStatus,
  canWrite = true,
  canComment = false,
  isGroup = false,
  ticketWhatsapp = null,
}) => {
  const classes = useStyles();
  const { ticketId } = useParams();

  const [medias, setMedias] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [showEmoji, setShowEmoji] = useState(false);
  const [loading, setLoading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [startingRecording, setStartingRecording] = useState(false);
  const [quickAnswers, setQuickAnswer] = useState([]);
  const [typeBar, setTypeBar] = useState(false);
  const [mentionCandidates, setMentionCandidates] = useState([]);
  const [mentionSuggestions, setMentionSuggestions] = useState([]);
  const [mentionState, setMentionState] = useState({
    open: false,
    start: 0,
    end: 0,
    query: "",
    activeIndex: 0,
  });
  const [selectedMentions, setSelectedMentions] = useState([]);
  const [wabaTemplateName, setWabaTemplateName] = useState("");
  const [wabaTemplateLanguage, setWabaTemplateLanguage] = useState("pt_BR");
  const [wabaTemplateBodyParams, setWabaTemplateBodyParams] = useState("");
  const [commentMode, setCommentMode] = useState(false);
  const [useWabaTemplate, setUseWabaTemplate] = useState(false);
  const [wabaTemplatesLoading, setWabaTemplatesLoading] = useState(false);
  const [wabaTemplateOptions, setWabaTemplateOptions] = useState([]);
  const [wabaTemplateKey, setWabaTemplateKey] = useState("");
  const inputRef = useRef();
  const [anchorEl, setAnchorEl] = useState(null);
  const { setReplyingMessage, replyingMessage } =
    useContext(ReplyMessageContext);
  const { user } = useContext(AuthContext);
  const mediaRecorderRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioMimeTypeRef = useRef("");

  const [signMessage, setSignMessage] = useLocalStorage("signOption", true);
  const isWabaConnection =
    String(ticketWhatsapp?.connectionType || "").toLowerCase() === "waba" &&
    Boolean(ticketWhatsapp?.wabaEnabled);
  const templateModeActive = !commentMode && isWabaConnection && useWabaTemplate;
  const canTypeMessage = canWrite || canComment;
  const selectedWabaTemplate = wabaTemplateOptions.find(
    item =>
      String(item.name) === String(wabaTemplateName) &&
      String(item.language) === String(wabaTemplateLanguage)
  );
  const templateNeedsBodyParameters =
    Number(selectedWabaTemplate?.bodyVariablesCount || 0) > 0;

  const getTemplateLabel = template => {
    const category = String(template?.category || "").trim().toUpperCase();
    const name = String(template?.name || "").trim();
    const language = String(template?.language || "").trim();
    return `${category ? `[${category}] ` : ""}${name} (${language})`;
  };

  const normalizeMentionText = value =>
    String(value || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase();

  const releaseRecorderResources = () => {
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
    }

    mediaStreamRef.current = null;
    mediaRecorderRef.current = null;
    audioChunksRef.current = [];
    audioMimeTypeRef.current = "";
  };

  const stopRecordingAndGetBlob = () =>
    new Promise((resolve, reject) => {
      const recorder = mediaRecorderRef.current;
      const mimeType =
        audioMimeTypeRef.current || recorder?.mimeType || "audio/webm";

      if (!recorder) {
        resolve(new Blob([], { type: mimeType }));
        return;
      }

      if (recorder.state === "inactive") {
        const blob = new Blob(audioChunksRef.current, { type: mimeType });
        releaseRecorderResources();
        resolve(blob);
        return;
      }

      recorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: mimeType });
        releaseRecorderResources();
        resolve(blob);
      };

      recorder.onerror = event => {
        const error = event?.error || new Error("Falha ao finalizar a gravação.");
        releaseRecorderResources();
        reject(error);
      };

      recorder.stop();
    });

  useEffect(() => {
    inputRef.current.focus();
  }, [replyingMessage]);

  useEffect(() => {
    inputRef.current.focus();
    return () => {
      setInputMessage("");
      setSelectedMentions([]);
      setMentionState({
        open: false,
        start: 0,
        end: 0,
        query: "",
        activeIndex: 0,
      });
      setMentionSuggestions([]);
      setShowEmoji(false);
      setMedias([]);
      setUseWabaTemplate(false);
      setWabaTemplateName("");
      setWabaTemplateLanguage("pt_BR");
      setWabaTemplateBodyParams("");
      setWabaTemplateOptions([]);
      setWabaTemplateKey("");
      setReplyingMessage(null);
      releaseRecorderResources();
    };
  }, [ticketId, setReplyingMessage]);

  useEffect(() => {
    let isMounted = true;

    const loadWabaTemplates = async () => {
      if (!isWabaConnection || !useWabaTemplate || !ticketWhatsapp?.id) {
        if (isMounted) {
          setWabaTemplateOptions([]);
          setWabaTemplatesLoading(false);
          setWabaTemplateKey("");
        }
        return;
      }

      setWabaTemplatesLoading(true);
      try {
        const { data } = await api.get(
          `/whatsapp/${ticketWhatsapp.id}/waba/templates`
        );
        if (!isMounted) return;
        const options = Array.isArray(data?.templates) ? data.templates : [];
        setWabaTemplateOptions(options);
      } catch (err) {
        if (isMounted) {
          setWabaTemplateOptions([]);
        }
      } finally {
        if (isMounted) {
          setWabaTemplatesLoading(false);
        }
      }
    };

    loadWabaTemplates();

    return () => {
      isMounted = false;
    };
  }, [isWabaConnection, useWabaTemplate, ticketWhatsapp?.id]);

  useEffect(() => {
    if (!canComment && commentMode) {
      setCommentMode(false);
    }
    if (canComment && !canWrite && !commentMode) {
      setCommentMode(true);
    }
  }, [canComment, canWrite, commentMode]);

  useEffect(() => () => {
    releaseRecorderResources();
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadMentionCandidates = async () => {
      if (!isGroup || !ticketId) {
        if (isMounted) {
          setMentionCandidates([]);
        }
        return;
      }

      try {
        const { data } = await api.get(`/messages/${ticketId}/mentions`);
        if (!isMounted) return;

        const mentions = Array.isArray(data?.mentions) ? data.mentions : [];
        setMentionCandidates(mentions);
      } catch (err) {
        if (isMounted) {
          setMentionCandidates([]);
        }
      }
    };

    loadMentionCandidates();

    return () => {
      isMounted = false;
    };
  }, [ticketId, isGroup]);

  const closeMentionSuggestions = () => {
    setMentionState({
      open: false,
      start: 0,
      end: 0,
      query: "",
      activeIndex: 0,
    });
    setMentionSuggestions([]);
  };

  const getMentionSuggestions = query => {
    const normalizedQuery = normalizeMentionText(query);
    const candidates = mentionCandidates.filter(candidate => {
      const name = normalizeMentionText(candidate.name);
      const number = String(candidate.number || "").replace(/\D/g, "");
      if (!normalizedQuery) return true;
      return name.includes(normalizedQuery) || number.startsWith(normalizedQuery);
    });

    return candidates.slice(0, 8);
  };

  const detectMentionAtCursor = (value, cursorPosition) => {
    if (!isGroup || !canWrite) {
      closeMentionSuggestions();
      return;
    }

    const safeCursor =
      typeof cursorPosition === "number" ? cursorPosition : value.length;
    const beforeCursor = value.slice(0, safeCursor);
    const match = beforeCursor.match(/(?:^|[\s\n])@([^\s@]{0,40})$/);

    if (!match) {
      closeMentionSuggestions();
      return;
    }

    const query = match[1] || "";
    const suggestions = getMentionSuggestions(query);

    if (!suggestions.length) {
      closeMentionSuggestions();
      return;
    }

    setMentionSuggestions(suggestions);
    setMentionState({
      open: true,
      query,
      start: safeCursor - query.length - 1,
      end: safeCursor,
      activeIndex: 0,
    });
  };

  const applyMentionSuggestion = candidate => {
    if (!candidate || !mentionState.open) return;

    const before = inputMessage.slice(0, mentionState.start);
    const after = inputMessage.slice(mentionState.end);
    const mentionLabel = `@${candidate.name}`;
    const nextMessage = `${before}${mentionLabel} ${after}`;

    setInputMessage(nextMessage);
    setSelectedMentions(prevState => {
      const normalizedJid = String(candidate.jid || "").trim();
      if (!normalizedJid) return prevState;

      const alreadyIncluded = prevState.some(
        item => String(item.jid) === normalizedJid
      );

      if (alreadyIncluded) {
        return prevState;
      }

      return [
        ...prevState,
        {
          jid: normalizedJid,
          name: String(candidate.name || "").trim(),
          number: String(candidate.number || "").trim(),
        },
      ];
    });

    closeMentionSuggestions();

    const nextCursorPosition = `${before}${mentionLabel} `.length;
    setTimeout(() => {
      if (inputRef.current?.focus) {
        inputRef.current.focus();
      }
      if (inputRef.current?.setSelectionRange) {
        inputRef.current.setSelectionRange(nextCursorPosition, nextCursorPosition);
      }
    }, 0);
  };

  const handleChangeInput = e => {
    const value = e.target.value;
    const cursorPosition = e.target.selectionStart;

    setInputMessage(value);
    handleLoadQuickAnswer(value);
    detectMentionAtCursor(value, cursorPosition);
  };

  const handleQuickAnswersClick = value => {
    setInputMessage(value);
    setTypeBar(false);
  };

  const handleAddEmoji = e => {
    let emoji = e.native;
    setInputMessage(prevState => prevState + emoji);
  };

  const handleChangeMedias = e => {
    if (!e.target.files) {
      return;
    }

    const selectedMedias = Array.from(e.target.files);
    setMedias(selectedMedias);
  };

  const handleInputPaste = e => {
    if (e.clipboardData.files[0]) {
      setMedias([e.clipboardData.files[0]]);
    }
  };

  const handleUploadMedia = async e => {
    setLoading(true);
    e.preventDefault();

    const formData = new FormData();
    formData.append("fromMe", true);
    medias.forEach(media => {
      formData.append("medias", media);
      formData.append("body", media.name);
    });

    try {
      await api.post(`/messages/${ticketId}`, formData);
    } catch (err) {
      toastError(err);
    }

    setLoading(false);
    setMedias([]);
  };

  const handleSendMessage = async () => {
    if (commentMode) {
      if (inputMessage.trim() === "") return;
      setLoading(true);
      closeMentionSuggestions();

      const parsedBody = signMessage
        ? `*${user?.name}:*\n${inputMessage.trim()}`
        : inputMessage.trim();

      try {
        await api.post(`/messages/${ticketId}/comment`, { body: parsedBody });
      } catch (err) {
        toastError(err);
      }

      setInputMessage("");
      setSelectedMentions([]);
      setShowEmoji(false);
      setLoading(false);
      setReplyingMessage(null);
      return;
    }

    const hasTemplateData =
      isWabaConnection &&
      useWabaTemplate &&
      Boolean(String(wabaTemplateName || "").trim());
    if (inputMessage.trim() === "" && !(isWabaConnection && hasTemplateData)) return;
    setLoading(true);
    closeMentionSuggestions();

    const parsedBody = signMessage
      ? `*${user?.name}:*\n${inputMessage.trim()}`
      : inputMessage.trim();
    const normalizedBody = normalizeMentionText(parsedBody);
    const mentions = selectedMentions
      .filter(item => {
        const mentionName = normalizeMentionText(item?.name);
        const mentionNumber = String(item?.number || "").replace(/\D/g, "");

        const hasMentionByName =
          mentionName && normalizedBody.includes(`@${mentionName}`);
        const hasMentionByNumber =
          mentionNumber && normalizedBody.includes(`@${mentionNumber}`);

        return hasMentionByName || hasMentionByNumber;
      })
      .map(item => item.jid)
      .filter(Boolean);

    const message = {
      read: 1,
      fromMe: true,
      mediaUrl: "",
      body: parsedBody,
      quotedMsg: replyingMessage,
      mentions,
    };

    if (hasTemplateData) {
      const bodyParameters = templateNeedsBodyParameters
        ? String(wabaTemplateBodyParams || "")
            .split("|")
            .map(item => item.trim())
            .filter(Boolean)
        : [];

      message.wabaTemplate = {
        name: String(wabaTemplateName || "").trim(),
        languageCode: String(wabaTemplateLanguage || "").trim() || "pt_BR",
        bodyParameters,
      };
    }

    try {
      await api.post(`/messages/${ticketId}`, message);
    } catch (err) {
      toastError(err);
    }

    setInputMessage("");
    setSelectedMentions([]);
    if (isWabaConnection && useWabaTemplate) {
      setWabaTemplateName("");
      setWabaTemplateBodyParams("");
      setWabaTemplateKey("");
    }
    setShowEmoji(false);
    setLoading(false);
    setReplyingMessage(null);
  };

  const handleStartRecording = async () => {
    setLoading(true);
    setStartingRecording(true);
    try {
      if (
        typeof navigator === "undefined" ||
        !navigator.mediaDevices?.getUserMedia ||
        typeof window === "undefined" ||
        typeof window.MediaRecorder === "undefined"
      ) {
        throw new Error("Gravação de áudio não suportada neste navegador.");
      }

      releaseRecorderResources();

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = getSupportedAudioMimeType();
      const recorder = mimeType
        ? new window.MediaRecorder(stream, { mimeType })
        : new window.MediaRecorder(stream);

      mediaStreamRef.current = stream;
      mediaRecorderRef.current = recorder;
      audioChunksRef.current = [];
      audioMimeTypeRef.current = recorder.mimeType || mimeType || "audio/webm";

      recorder.onstart = () => {
        setRecording(true);
        setStartingRecording(false);
        setLoading(false);
      };

      recorder.ondataavailable = event => {
        if (event.data && event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      recorder.onerror = () => {
        releaseRecorderResources();
        setRecording(false);
        setStartingRecording(false);
        setLoading(false);
      };

      recorder.start();
    } catch (err) {
      toastError(err);
      setStartingRecording(false);
      setLoading(false);
    }
  };

  const handleLoadQuickAnswer = async value => {
    if (value && value.indexOf("/") === 0) {
      try {
        const { data } = await api.get("/quickAnswers/", {
          params: { searchParam: value.substring(1) },
        });
        setQuickAnswer(data.quickAnswers);
        if (data.quickAnswers.length > 0) {
          setTypeBar(true);
        } else {
          setTypeBar(false);
        }
      } catch (err) {
        setTypeBar(false);
      }
    } else {
      setTypeBar(false);
    }
  };

  const handleUploadAudio = async () => {
    setLoading(true);
    try {
      const blob = await stopRecordingAndGetBlob();
      if (blob.size < 10000) {
        setLoading(false);
        setRecording(false);
        setStartingRecording(false);
        return;
      }

      const formData = new FormData();
      const extension = getAudioFileExtension(blob.type);
      const filename = `${new Date().getTime()}.${extension}`;
      formData.append("medias", blob, filename);
      formData.append("fromMe", true);

      await api.post(`/messages/${ticketId}`, formData);
    } catch (err) {
      toastError(err);
    }

    setRecording(false);
    setStartingRecording(false);
    setLoading(false);
  };

  const handleCancelAudio = async () => {
    try {
      if (mediaRecorderRef.current?.state === "recording") {
        await stopRecordingAndGetBlob();
      } else {
        releaseRecorderResources();
      }
      setRecording(false);
      setStartingRecording(false);
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenMenuClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleInputKeyDown = e => {
    if (mentionState.open && mentionSuggestions.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setMentionState(prevState => ({
          ...prevState,
          activeIndex: (prevState.activeIndex + 1) % mentionSuggestions.length,
        }));
        return;
      }

      if (e.key === "ArrowUp") {
        e.preventDefault();
        setMentionState(prevState => ({
          ...prevState,
          activeIndex:
            (prevState.activeIndex - 1 + mentionSuggestions.length) %
            mentionSuggestions.length,
        }));
        return;
      }

      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        const candidate = mentionSuggestions[mentionState.activeIndex];
        applyMentionSuggestion(candidate);
        return;
      }

      if (e.key === "Escape") {
        e.preventDefault();
        closeMentionSuggestions();
        return;
      }
    }

    if (
      e.key === "Enter" &&
      !e.shiftKey &&
      !loading &&
      canTypeMessage
    ) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleMenuItemClick = event => {
    setAnchorEl(null);
  };

  const renderReplyingMessage = message => {
    return (
      <div className={classes.replyginMsgWrapper}>
        <div className={classes.replyginMsgContainer}>
          <span
            className={clsx(classes.replyginContactMsgSideColor, {
              [classes.replyginSelfMsgSideColor]: !message.fromMe,
            })}
          ></span>
          <div className={classes.replyginMsgBody}>
            {!message.fromMe && (
              <span className={classes.messageContactName}>
                {message.contact?.name}
              </span>
            )}
            {message.body}
          </div>
        </div>
        <IconButton
          aria-label="showRecorder"
          component="span"
          disabled={loading || ticketStatus !== "open"}
          onClick={() => setReplyingMessage(null)}
        >
          <ClearIcon className={classes.sendMessageIcons} />
        </IconButton>
      </div>
    );
  };

  if (medias.length > 0)
    return (
      <Paper elevation={0} square className={classes.viewMediaInputWrapper}>
        <IconButton
          aria-label="cancel-upload"
          component="span"
          onClick={e => setMedias([])}
        >
          <CancelIcon className={classes.sendMessageIcons} />
        </IconButton>

        {loading ? (
          <div>
            <CircularProgress className={classes.circleLoading} />
          </div>
        ) : (
          <span>
            {medias[0]?.name}
            {/* <img src={media.preview} alt=""></img> */}
          </span>
        )}
        <IconButton
          aria-label="send-upload"
          component="span"
          onClick={handleUploadMedia}
          disabled={loading}
        >
          <SendIcon className={classes.sendMessageIcons} />
        </IconButton>
      </Paper>
    );
  else {
    return (
      <Paper square elevation={0} className={classes.mainWrapper}>
        {!canWrite && !canComment && ticketStatus === "open" && (
          <Typography className={classes.readOnlyNotice}>
            Este chamado pertence a outro atendente. Você pode visualizar, mas não enviar mensagens.
          </Typography>
        )}
        {!canWrite && canComment && ticketStatus === "open" && (
          <Typography className={classes.readOnlyNotice}>
            Este chamado pertence a outro atendente. Voce pode inserir apenas comentario interno.
          </Typography>
        )}
        {canComment && (
          <div
            style={{
              width: "100%",
              padding: "6px 12px 0",
              display: "flex",
            }}
          >
            <FormControlLabel
              className={classes.toggleControl}
              label="Comentario interno (bolha amarela)"
              control={
                <Switch
                  size="small"
                  checked={commentMode}
                  onChange={e => setCommentMode(e.target.checked)}
                  color="primary"
                  disabled={loading || ticketStatus !== "open"}
                />
              }
            />
          </div>
        )}
        {replyingMessage && renderReplyingMessage(replyingMessage)}
        {isWabaConnection && !commentMode && (
          <div
            style={{
              width: "100%",
              padding: "6px 12px 0",
              display: "flex",
              flexDirection: "column",
              gap: 6,
            }}
          >
            <FormControlLabel
              className={classes.toggleControl}
              label="Usar template WABA"
              control={
                <Switch
                  size="small"
                  checked={useWabaTemplate}
                  onChange={e => setUseWabaTemplate(e.target.checked)}
                  color="primary"
                  disabled={!canWrite || loading || ticketStatus !== "open"}
                />
              }
            />
            {useWabaTemplate && (
              <>
                <InputBase
                  className={classes.messageInput}
                  classes={{ root: classes.templateInput }}
                  inputProps={{ list: "waba-template-options" }}
                  placeholder={
                    wabaTemplatesLoading
                      ? "Carregando templates..."
                      : "Escolha template aprovado (ou digite nome__idioma)"
                  }
                  value={wabaTemplateKey}
                  onChange={e => {
                    const value = e.target.value;
                    setWabaTemplateKey(value);

                    const selected = wabaTemplateOptions.find(
                      item => `${item.name}__${item.language}` === value
                    );

                    if (selected) {
                      setWabaTemplateName(String(selected.name || ""));
                      setWabaTemplateLanguage(String(selected.language || "pt_BR"));
                    } else {
                      const [manualName = "", manualLanguage = "pt_BR"] = String(
                        value || ""
                      ).split("__");
                      setWabaTemplateName(String(manualName || "").trim());
                      setWabaTemplateLanguage(
                        String(manualLanguage || "pt_BR").trim() || "pt_BR"
                      );
                    }
                  }}
                  disabled={!canWrite || loading || ticketStatus !== "open"}
                />
                <datalist id="waba-template-options">
                  {wabaTemplateOptions.map(item => (
                    <option
                      key={`${item.name}__${item.language}`}
                      value={`${item.name}__${item.language}`}
                    >
                      {getTemplateLabel(item)}
                    </option>
                  ))}
                </datalist>
                {selectedWabaTemplate && (
                  <Typography
                    variant="caption"
                    className={classes.templateHint}
                  >
                    Tipo: {String(selectedWabaTemplate.category || "-").toUpperCase()}{" "}
                    {Number(selectedWabaTemplate.bodyVariablesCount || 0) > 0
                      ? `| Variaveis body: ${selectedWabaTemplate.bodyVariablesCount}`
                      : "| Sem variaveis no body"}
                  </Typography>
                )}
                {templateNeedsBodyParameters && (
                <InputBase
                  className={classes.messageInput}
                  classes={{ root: classes.templateInput }}
                  placeholder="Par?metros do body (separados por |)"
                  value={wabaTemplateBodyParams}
                  onChange={e => setWabaTemplateBodyParams(e.target.value)}
                  disabled={!canWrite || loading || ticketStatus !== "open"}
                />
                )}
              </>
            )}
          </div>
        )}
        <div className={classes.newMessageBox}>
          <Hidden only={["sm", "xs"]}>
            <IconButton
              aria-label="emojiPicker"
              component="span"
              disabled={loading || recording || startingRecording || ticketStatus !== "open" || !canTypeMessage || templateModeActive || commentMode}
              onClick={e => setShowEmoji(prevState => !prevState)}
            >
              <MoodIcon className={classes.sendMessageIcons} />
            </IconButton>
            {showEmoji ? (
              <div className={classes.emojiBox}>
                <ClickAwayListener onClickAway={e => setShowEmoji(false)}>
                  <Picker
                    perLine={16}
                    showPreview={false}
                    showSkinTones={false}
                    onSelect={handleAddEmoji}
                  />
                </ClickAwayListener>
              </div>
            ) : null}

            <input
              multiple
              type="file"
              id="upload-button"
              disabled={loading || recording || startingRecording || ticketStatus !== "open" || !canWrite || templateModeActive || commentMode}
              className={classes.uploadInput}
              onChange={handleChangeMedias}
            />
            <label htmlFor="upload-button">
              <IconButton
                  aria-label="upload"
                  component="span"
                  disabled={loading || recording || startingRecording || ticketStatus !== "open" || !canWrite || templateModeActive || commentMode}
                >
                <AttachFileIcon className={classes.sendMessageIcons} />
              </IconButton>
            </label>
            <FormControlLabel
              className={classes.toggleControl}
              label={i18n.t("messagesInput.signMessage")}
              labelPlacement="start"
              control={
                <Switch
                  size="small"
                  checked={signMessage}
                  onChange={e => {
                    setSignMessage(e.target.checked);
                  }}
                  name="showAllTickets"
                  color="primary"
                  disabled={!canTypeMessage || templateModeActive}
                />
              }
            />
          </Hidden>
          <Hidden only={["md", "lg", "xl"]}>
            <IconButton
              aria-controls="simple-menu"
              aria-haspopup="true"
              disabled={!canTypeMessage}
              onClick={handleOpenMenuClick}
            >
              <MoreVert></MoreVert>
            </IconButton>
            <Menu
              id="simple-menu"
              keepMounted
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuItemClick}
            >
              <MenuItem onClick={handleMenuItemClick}>
                <IconButton
                  aria-label="emojiPicker"
                  component="span"
                  disabled={loading || recording || startingRecording || ticketStatus !== "open" || !canTypeMessage || templateModeActive || commentMode}
                  onClick={e => setShowEmoji(prevState => !prevState)}
                >
                  <MoodIcon className={classes.sendMessageIcons} />
                </IconButton>
              </MenuItem>
              <MenuItem onClick={handleMenuItemClick}>
                <input
                  multiple
                  type="file"
                  id="upload-button"
                  disabled={loading || recording || startingRecording || ticketStatus !== "open" || !canWrite || templateModeActive || commentMode}
                  className={classes.uploadInput}
                  onChange={handleChangeMedias}
                />
                <label htmlFor="upload-button">
                  <IconButton
                    aria-label="upload"
                    component="span"
                    disabled={loading || recording || startingRecording || ticketStatus !== "open" || !canWrite || templateModeActive || commentMode}
                  >
                    <AttachFileIcon className={classes.sendMessageIcons} />
                  </IconButton>
                </label>
              </MenuItem>
              <MenuItem onClick={handleMenuItemClick}>
                <FormControlLabel
                  className={classes.toggleControl}
                  label={i18n.t("messagesInput.signMessage")}
                  labelPlacement="start"
                  control={
                    <Switch
                      size="small"
                      checked={signMessage}
                      onChange={e => {
                        setSignMessage(e.target.checked);
                      }}
                      name="showAllTickets"
                      color="primary"
                      disabled={!canTypeMessage || templateModeActive}
                    />
                  }
                />
              </MenuItem>
            </Menu>
          </Hidden>
          <div className={classes.messageInputWrapper}>
            {templateModeActive ? (
              <Typography
                variant="body2"
                className={classes.templateModeNotice}
              >
                Modo template WABA ativo. O campo de mensagem livre foi desabilitado.
              </Typography>
            ) : (
              <>
                <InputBase
                  inputRef={input => {
                    input && input.focus();
                    input && (inputRef.current = input);
                  }}
                  className={classes.messageInput}
                  placeholder={
                    commentMode
                      ? "Digite um comentario interno..."
                      : ticketStatus === "open"
                      ? i18n.t("messagesInput.placeholderOpen")
                      : i18n.t("messagesInput.placeholderClosed")
                  }
                  multiline
                  maxRows={5}
                  value={inputMessage}
                  onChange={handleChangeInput}
                  disabled={recording || startingRecording || loading || ticketStatus !== "open"}
                  readOnly={!canTypeMessage}
                  onPaste={e => {
                    ticketStatus === "open" && canWrite && !commentMode && handleInputPaste(e);
                  }}
                  onClick={e => detectMentionAtCursor(inputMessage, e.target.selectionStart)}
                  onKeyDown={handleInputKeyDown}
                />
                {mentionState.open && mentionSuggestions.length > 0 ? (
                  <ClickAwayListener onClickAway={closeMentionSuggestions}>
                    <List className={classes.mentionSuggestionsWrapper}>
                      {mentionSuggestions.map((candidate, index) => (
                        <ListItem
                          button
                          dense
                          key={`${candidate.jid}-${candidate.contactId}`}
                          onClick={() => applyMentionSuggestion(candidate)}
                          className={clsx(classes.mentionSuggestionItem, {
                            [classes.mentionSuggestionItemActive]:
                              index === mentionState.activeIndex,
                          })}
                        >
                          <ListItemText
                            primary={candidate.name}
                            secondary={candidate.number || candidate.jid}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </ClickAwayListener>
                ) : typeBar ? (
                  <ul className={classes.messageQuickAnswersWrapper}>
                    {quickAnswers.map((value, index) => {
                      return (
                        <li
                          className={classes.messageQuickAnswersWrapperItem}
                          key={index}
                        >
                          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                          <a onClick={() => handleQuickAnswersClick(value.message)}>
                            {`${value.shortcut} - ${value.message}`}
                          </a>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <div></div>
                )}
              </>
            )}
          </div>
          {(templateModeActive || inputMessage) ? (
            <IconButton
              aria-label="sendMessage"
              component="span"
              onClick={handleSendMessage}
              disabled={
                loading ||
                !canTypeMessage ||
                (templateModeActive && !String(wabaTemplateName || "").trim())
              }
            >
              <SendIcon className={classes.sendMessageIcons} />
            </IconButton>
          ) : recording ? (
            <div className={classes.recorderWrapper}>
              <IconButton
                aria-label="cancelRecording"
                component="span"
                fontSize="large"
                disabled={loading}
                onClick={handleCancelAudio}
              >
                <HighlightOffIcon className={classes.cancelAudioIcon} />
              </IconButton>
              {loading ? (
                <div>
                  <CircularProgress className={classes.audioLoading} />
                </div>
              ) : (
                <RecordingTimer />
              )}

              <IconButton
                aria-label="sendRecordedAudio"
                component="span"
                onClick={handleUploadAudio}
                disabled={loading}
              >
                <CheckCircleOutlineIcon className={classes.sendAudioIcon} />
              </IconButton>
            </div>
          ) : (
              <IconButton
                aria-label="showRecorder"
                component="span"
              disabled={loading || startingRecording || ticketStatus !== "open" || !canWrite || templateModeActive || commentMode}
              onClick={handleStartRecording}
            >
              {startingRecording ? (
                <CircularProgress size={22} className={classes.audioLoading} />
              ) : (
                <MicIcon className={classes.sendMessageIcons} />
              )}
            </IconButton>
          )}
        </div>
      </Paper>
    );
  }
};

export default MessageInput;
